import React, { useEffect, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import SockJS from "sockjs-client";
import { Stomp } from "@stomp/stompjs";
import Avatar from "@mui/material/Avatar";
import CircularProgress from "@mui/material/CircularProgress";
import SendIcon from "@mui/icons-material/Send";

// API fonksiyonu
async function getChatHistory(chatRoomId) {
  const tokenObj = JSON.parse(localStorage.getItem('auth-storage'));
  const token = tokenObj?.state?.accessToken;
  const res = await fetch(`/v1/chat/history/${chatRoomId}`, {
    headers: {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json'
    }
  });
  if (!res.ok) return [];
  return await res.json();
}

// Oda bazında okundu yap
async function markAllMessagesAsRead(chatRoomId) {
  const tokenObj = JSON.parse(localStorage.getItem('auth-storage'));
  const token = tokenObj?.state?.accessToken;
  await fetch(`/v1/chat/readAll/${chatRoomId}`, {
    method: "PUT",
    headers: {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json'
    }
  });
}

const WS_URL = "https://dersmaster.com/ws";

const ChatRoomPage = ({
  chatRoomId,
  receiverUsername: initialReceiverUsername,
  chatRoomType: initialChatRoomType,
}) => {
  const location = useLocation();
  const params = new URLSearchParams(location.search);
  const fromChatListReceiverUsername = params.get("receiverUsername") || initialReceiverUsername;

  const [messages, setMessages] = useState([]);
  const [content, setContent] = useState("");
  const [stompClient, setStompClient] = useState(null);
  const [chatRoomType, setChatRoomType] = useState(null);
  const [receiverUsername, setReceiverUsername] = useState(fromChatListReceiverUsername);
  const [receiverName, setReceiverName] = useState("");
  const [receiverSurname, setReceiverSurname] = useState("");
  const [receiverProfileImage, setReceiverProfileImage] = useState("");
  const [loading, setLoading] = useState(true);
  const [isFirstLoad, setIsFirstLoad] = useState(true); // İlk yükleme kontrolü
  const [onlineUsers, setOnlineUsers] = useState(new Set()); // Online kullanıcıları takip et

  const messagesEndRef = useRef(null);
  const messagesContainerRef = useRef(null);
  const isUserAtBottom = useRef(true);

  const userStr = localStorage.getItem('user');
  const user = userStr ? JSON.parse(userStr) : null;
  const username = user?.username;

  // Kullanıcının online olup olmadığını kontrol et
  const isUserOnline = (username) => {
    return onlineUsers.has(username);
  };

  // Mesaj geçmişini çek + okundu bildir!
  useEffect(() => {
    if (chatRoomId) {
      markAllMessagesAsRead(chatRoomId);
      setLoading(true);
      getChatHistory(chatRoomId).then(async msgs => {
        if (msgs.length > 0) {
          const firstMsg = msgs[0];
          setChatRoomType((firstMsg.chatRoomType || "PRIVATE").toUpperCase());
          if ((firstMsg.chatRoomType || "PRIVATE").toUpperCase() === "PRIVATE") {
            // Karşı tarafın bilgilerini doğru şekilde belirle
            let otherUser = null;
            
            // Eğer bu mesajın göndereni ben değilsem, gönderen karşı taraftır
            if (firstMsg.senderUsername !== username) {
              otherUser = {
                username: firstMsg.senderUsername,
                name: firstMsg.senderName,
                surname: firstMsg.senderSurname,
                profileImage: firstMsg.senderProfileImageUrl
              };
            } 
            // Eğer gönderen bensem, alıcı karşı taraftır
            else if (firstMsg.receiverUsername !== username) {
              otherUser = {
                username: firstMsg.receiverUsername,
                name: firstMsg.receiverName,
                surname: firstMsg.receiverSurname,
                profileImage: firstMsg.receiverProfileImageUrl
              };
            }
            
            // URL'den gelen receiverUsername'i öncelikli tut
            if (fromChatListReceiverUsername && !otherUser) {
              setReceiverUsername(fromChatListReceiverUsername);
            } else if (otherUser) {
              setReceiverUsername(otherUser.username);
              setReceiverName(otherUser.name);
              setReceiverSurname(otherUser.surname);
              setReceiverProfileImage(otherUser.profileImage);
            }
          }
        }
        setMessages(msgs);
        setLoading(false);
        // İlk yükleme tamamlandığında scroll'u açık bırak
        setTimeout(() => setIsFirstLoad(false), 500);
      });
    }
  }, [chatRoomId, username]);

  // WebSocket bağlantısı kur
  useEffect(() => {
    if (!username) return;
    const socket = new SockJS(WS_URL);
    const client = Stomp.over(socket);

    client.debug = () => {};

    const tokenObj = JSON.parse(localStorage.getItem('auth-storage'));
    const token = tokenObj?.state?.accessToken;

    // DİKKAT: headers parametresi 2. sırada, boş obje 3. sırada olmalı!
    client.connect(
      { Authorization: `Bearer ${token}` }, // headers
      () => {
        // Mesajları dinle
        client.subscribe("/topic/messages", async (msg) => {
          const body = JSON.parse(msg.body);
          if (body.chatRoomId === Number(chatRoomId)) {
            setMessages((prev) => [...prev, body]);
            await markAllMessagesAsRead(chatRoomId);
          }
          // Her yeni mesajda online kullanıcıları tekrar iste
          setTimeout(() => {
            if (client.connected) client.send("/app/getOnlineUsers", {}, "");
          }, 100);
        });

        // Online kullanıcıları dinle
        client.subscribe("/user/queue/online-users", (msg) => {
          const onlineUsersList = JSON.parse(msg.body);
          setOnlineUsers(new Set(onlineUsersList));
        });

        // Bağlantı kurulunca online kullanıcı listesini hemen iste
        if (client.connected) client.send("/app/getOnlineUsers", {}, "");
        // 1 saniye sonra tekrar iste (backend gecikmesi için)
        setTimeout(() => {
          if (client.connected) client.send("/app/getOnlineUsers", {}, "");
        }, 1000);
      }
    );
    setStompClient(client);

    return () => {
      client.disconnect();
    };
  }, [chatRoomId, username]);

  // Odaya ilk girildiğinde ve tab aktif olduğunda online kullanıcıları iste
  useEffect(() => {
    const refreshOnline = () => {
      if (stompClient && stompClient.connected) {
        stompClient.send("/app/getOnlineUsers", {}, "");
      }
    };
    refreshOnline();
    document.addEventListener('visibilitychange', refreshOnline);
    return () => document.removeEventListener('visibilitychange', refreshOnline);
  }, [stompClient, chatRoomId]);

  // Scroll pozisyonunu takip et
  useEffect(() => {
    const container = messagesContainerRef.current;
    if (!container) return;
    const handleScroll = () => {
      const threshold = 80; // px
      isUserAtBottom.current = container.scrollHeight - container.scrollTop - container.clientHeight < threshold;
    };
    container.addEventListener('scroll', handleScroll);
    return () => container.removeEventListener('scroll', handleScroll);
  }, []);

  // Scroll kontrolü - yeni mesaj geldiğinde sadece kullanıcı en alttaysa scroll et
  useEffect(() => {
    if (messages.length > 0 && isUserAtBottom.current) {
      setTimeout(() => {
        messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
      }, 100);
    }
  }, [messages, chatRoomId]);

  // Mesajı WebSocket ile gönder
  const handleSend = (e) => {
    e.preventDefault();
    if (!content.trim() || !stompClient) return;
    const msg = {
      chatRoomId: Number(chatRoomId),
      content: content,
      senderUsername: username,
      chatRoomType: (chatRoomType || "PRIVATE").toUpperCase(),
    };
    
    if (msg.chatRoomType === "PRIVATE") {
      msg.receiverUsername = receiverUsername;
    }
    stompClient.send("/app/send", {}, JSON.stringify(msg));
    setContent("");
    setTimeout(() => {
      messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
      // Mesajdan sonra online kullanıcıları tekrar iste
      if (stompClient && stompClient.connected) {
        stompClient.send("/app/getOnlineUsers", {}, "");
      }
    }, 150);
  };

  // Profil avatarına tıklanınca
  const handleAvatarClick = (msg) => {
    if (window.confirm("Hocanın profiline gitmek ister misiniz?")) {
      // TeacherID alanını backend mesajında nasıl geliyorsa öyle kullan!
      // Örneğin: msg.receiverId veya msg.teacherId veya başka bir id
      const teacherId = msg.receiverId || msg.teacherId || msg.id;
      if (teacherId) {
        window.location.href = `/profile/id/${teacherId}`;
      } else {
        alert("Profil bilgisi bulunamadı.");
      }
    }
  };

  // Karşı tarafın avatar harfi
  const otherUserLetter = receiverUsername?.[0]?.toUpperCase() ?? "?";

  // Koyu tema kontrolü
  const isDark = document.body.classList.contains("dark");

  // Online status komponenti
  const OnlineIndicator = ({ isOnline, size = "sm" }) => {
    const sizeClasses = {
      sm: "w-3 h-3",
      md: "w-4 h-4",
      lg: "w-5 h-5"
    };
    
    return (
      <div className={`${sizeClasses[size]} rounded-full border-2 border-white dark:border-gray-800 ${
        isOnline ? "bg-green-500" : "bg-gray-400"
      } shadow-sm`} />
    );
  };

  // Mesajlar arasındaki avatar ve isim gösterimi güncellemesi
  const ProfileAvatar = ({ profileImageUrl, name, surname, username, size = 40, fontSize = 20, className = "", onClick, showOnlineStatus = false }) => {
    const isOnline = showOnlineStatus ? isUserOnline(username) : false;
    
    return (
      <div className="relative">
        {profileImageUrl ? (
          <Avatar
            src={profileImageUrl}
            sx={{ width: size, height: size, cursor: onClick ? "pointer" : "default" }}
            className={className}
            onClick={onClick}
          />
        ) : (
          <Avatar
            sx={{
              bgcolor: isDark ? "#f59e0b" : "#3b82f6",
              width: size,
              height: size,
              fontSize: fontSize,
              cursor: onClick ? "pointer" : "default"
            }}
            className={className}
            onClick={onClick}
          >
            {name?.[0]?.toUpperCase() ?? username?.[0]?.toUpperCase() ?? "?"}
          </Avatar>
        )}
        {showOnlineStatus && (
          <div className="absolute -bottom-0.5 -right-0.5">
            <OnlineIndicator isOnline={isOnline} size="sm" />
          </div>
        )}
      </div>
    );
  };



  return (
    <div className="flex flex-col h-screen overflow-hidden bg-gradient-to-br from-blue-50 via-white to-purple-50 dark:from-gray-900 dark:via-gray-800 dark:to-gray-900">
      {/* Header */}
      <div className="sticky top-0 z-20 bg-white/90 dark:bg-gray-900/90 backdrop-blur-md border-b border-gray-200 dark:border-gray-700 shadow-lg">
        <div className="px-4 py-3 flex items-center gap-3">
          <ProfileAvatar
            profileImageUrl={receiverProfileImage}
            name={receiverName}
            surname={receiverSurname}
            username={receiverUsername}
            size={48}
            fontSize={24}
            showOnlineStatus={chatRoomType === "PRIVATE"}
          />
          <div className="flex-1">
            <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
              {receiverName 
                ? `${receiverName} ${receiverSurname}`
                : receiverUsername}
            </h2>
            <p className="text-sm text-gray-500 dark:text-gray-400">
              {chatRoomType === "GROUP" ? "Grup Sohbeti" : 
                isUserOnline(receiverUsername) ? "Çevrimiçi" : "Çevrimdışı"}
            </p>
          </div>
        </div>
      </div>

      {/* Messages Container */}
      <div className="flex-1 min-h-0 overflow-hidden">
        <div ref={messagesContainerRef} className="h-full overflow-y-auto px-4 py-4 space-y-4">
          {loading ? (
            <div className="flex items-center justify-center h-full">
              <div className="text-center">
                <CircularProgress size={40} className="text-blue-500" />
                <p className="mt-2 text-gray-500 dark:text-gray-400">Mesajlar yükleniyor...</p>
              </div>
            </div>
          ) : (
            <div className="space-y-4">
              {messages.length === 0 ? (
                <div className="text-center py-12">
                  <div className="text-gray-400 dark:text-gray-500 text-lg">
                    💬 Henüz mesaj yok
                  </div>
                  <p className="text-gray-500 dark:text-gray-400 mt-2">
                    İlk mesajı göndererek sohbeti başlatın!
                  </p>
                </div>
              ) : (
                messages.map((msg, i) => {
                  const isMe = msg.senderUsername === username;
                  return (
                    <div key={i} className={`flex ${isMe ? "justify-end" : "justify-start"} group`}>
                      {!isMe && (
                        <ProfileAvatar
                          profileImageUrl={msg.profileImageUrl}
                          name={msg.receiverName}
                          surname={msg.receiverSurname}
                          username={msg.senderUsername}
                          size={32}
                          fontSize={16}
                          className="mr-2 mt-auto"
                          onClick={() => handleAvatarClick(msg)}
                          showOnlineStatus={false}
                        />
                      )}
                      <div className="max-w-[75%] flex flex-col">
                        <div className={`
                          px-4 py-3 rounded-2xl shadow-sm
                          ${isMe
                            ? "bg-gradient-to-r from-blue-500 to-purple-600 text-white rounded-br-md"
                            : "bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 text-gray-900 dark:text-white rounded-bl-md"
                          }
                          group-hover:shadow-md transition-all duration-200
                        `}>
                          <p className="text-sm leading-relaxed break-words">
                            {msg.content}
                          </p>
                        </div>
                        <span className={`text-xs mt-1 px-2 ${
                          isMe ? "text-right text-gray-400" : "text-left text-gray-500"
                        }`}>
                          {new Date(msg.timestamp).toLocaleTimeString("tr-TR", {
                            hour: "2-digit",
                            minute: "2-digit",
                          })}
                        </span>
                      </div>
                    </div>
                  );
                })
              )}
              <div ref={messagesEndRef} />
            </div>
          )}
        </div>
      </div>

      {/* Message Input */}
      <div className="sticky bottom-0 bg-white/90 dark:bg-gray-900/90 backdrop-blur-md border-t border-gray-200 dark:border-gray-700 p-4">
        <form onSubmit={handleSend} className="flex items-center gap-3">
          <div className="flex-1 relative">
            <input
              type="text"
              className="w-full px-4 py-3 pr-12 rounded-full border border-gray-300 dark:border-gray-600 bg-gray-50 dark:bg-gray-800 text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
              placeholder="Mesajınızı yazın..."
              value={content}
              onChange={e => setContent(e.target.value)}
              autoFocus
            />
          </div>
          <button
            type="submit"
            disabled={!content.trim()}
            className={`p-3 rounded-full transition-all duration-200 ${
              content.trim()
                ? "bg-gradient-to-r from-blue-500 to-purple-600 text-white shadow-lg hover:shadow-xl hover:scale-105"
                : "bg-gray-300 dark:bg-gray-700 text-gray-500 dark:text-gray-400 cursor-not-allowed"
            }`}
          >
            <SendIcon className="w-5 h-5" />
          </button>
        </form>
      </div>
    </div>
  );
};

export default ChatRoomPage;
